import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import Notification from '../components/Notification';
import { useNotification } from '../hooks/useNotification';
import ConfirmModal from '../components/ConfirmModal';

const UserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isDistrict, isSchoolAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    schoolDistrict: '',
    role: '',
    status: '',
    schoolId: '',
    districtId: '',
    password: '',
  });
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [schools, setSchools] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false });
  const { notification, showError, showSuccess, hideNotification } = useNotification();

  useEffect(() => {
    fetchUserDetails();
    if (isAdmin) {
      fetchSchools();
      fetchDistricts();
    }
  }, [id, isAdmin]);

  const fetchUserDetails = async () => {
    try {
      const response = await api.get(`/admin/users/${id}`);
      const data = response.data.data;
      setUserData(data);
      setEditForm({
        firstName: data.user.firstName,
        lastName: data.user.lastName,
        email: data.user.email,
        phoneNumber: data.user.phoneNumber || '',
        schoolDistrict: data.user.schoolDistrict || '',
        role: data.user.role,
        status: data.user.status,
        schoolId: data.user.school?.id || '',
        districtId: data.user.districtId || '',
        password: '',
      });
      setShowPasswordField(false);
    } catch (error) {
      console.error('Failed to fetch user details:', error);
      showError('Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const fetchSchools = async () => {
    try {
      const response = await api.get('/admin/schools');
      setSchools(response.data.data.schools || []);
    } catch (error) {
      console.error('Failed to fetch schools:', error);
    }
  };

  const fetchDistricts = async () => {
    try {
      const response = await api.get('/admin/districts');
      setDistricts(response.data.data.districts || []);
    } catch (error) {
      console.error('Failed to fetch districts:', error);
    }
  };

  const handleSave = async () => {
    try {
      // Validate password if provided
      if (showPasswordField && editForm.password) {
        if (editForm.password.length < 6) {
          showError('Password must be at least 6 characters long');
          return;
        }
      }

      const payload = { ...editForm };
      // Only include password if it's provided
      if (!showPasswordField || !editForm.password) {
        delete payload.password;
      }

      await api.patch(`/admin/users/${id}`, payload);
      setIsEditing(false);
      setShowPasswordField(false);
      setEditForm({ ...editForm, password: '' });
      fetchUserDetails();
      showSuccess('User updated successfully');
    } catch (error) {
      console.error('Failed to update user:', error);
      showError(error.response?.data?.message || 'Failed to update user');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setShowPasswordField(false);
    if (userData) {
      setEditForm({
        firstName: userData.user.firstName,
        lastName: userData.user.lastName,
        email: userData.user.email,
        phoneNumber: userData.user.phoneNumber || '',
        schoolDistrict: userData.user.schoolDistrict || '',
        role: userData.user.role,
        status: userData.user.status,
        schoolId: userData.user.school?.id || '',
        districtId: userData.user.districtId || '',
        password: '',
      });
    }
  };

  const handleDeleteUser = async () => {
    try {
      await api.delete(`/admin/users/${id}`);
      setDeleteModal({ isOpen: false });
      showSuccess('User deleted successfully');
      navigate('/users');
    } catch (error) {
      console.error('Failed to delete user:', error);
      showError(error.response?.data?.message || 'Failed to delete user');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      ACTIVE: { bg: 'bg-green-100', text: 'text-green-800', label: 'Active' },
      PENDING_VERIFICATION: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
      SUSPENDED: { bg: 'bg-red-100', text: 'text-red-800', label: 'Suspended' },
      INACTIVE: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Inactive' },
    };
    const config = statusConfig[status] || statusConfig.INACTIVE;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      ADMIN: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Admin' },
      SCHOOL_ADMIN: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Principal' },
      DISTRICT: { bg: 'bg-indigo-100', text: 'text-indigo-800', label: 'District Manager' },
      TEACHER: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Teacher' },
    };
    const config = roleConfig[role] || roleConfig.TEACHER;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading user details...</p>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">User not found</p>
        <button
          onClick={() => navigate('/users')}
          className="mt-4 text-primary hover:text-primaryDark"
        >
          Back to Users
        </button>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'enrollments', label: 'Enrollments' },
    { id: 'progress', label: 'Progress' },
    { id: 'certificates', label: 'Certificates' },
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/users')}
            className="text-gray-600 hover:text-gray-800"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-800">
              {userData.user.firstName} {userData.user.lastName}
            </h1>
            <p className="text-gray-600">{userData.user.email}</p>
          </div>
        </div>
        {!isEditing && isAdmin && (
          <div className="flex space-x-3">
            <button
              onClick={() => {
                setIsEditing(true);
                setEditForm({ ...editForm, password: '' });
                setShowPasswordField(false);
                setShowPassword(false);
              }}
              className="bg-primary text-white px-6 py-2 rounded-lg font-semibold hover:bg-primaryDark transition-colors"
            >
              Edit User
            </button>
            <button
              onClick={() => setDeleteModal({ isOpen: true })}
              className="bg-red-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-red-700 transition-colors"
            >
              Delete User
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-md mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl shadow-md p-6">
        {activeTab === 'overview' && (
          <div>
            {isEditing ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={editForm.firstName}
                      onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      value={editForm.lastName}
                      onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={editForm.phoneNumber}
                      onChange={(e) => setEditForm({ ...editForm, phoneNumber: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      School District
                    </label>
                    <input
                      type="text"
                      value={editForm.schoolDistrict}
                      onChange={(e) => setEditForm({ ...editForm, schoolDistrict: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  {isAdmin && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Role *
                        </label>
                        <select
                          value={editForm.role}
                          onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                          <option value="TEACHER">Teacher</option>
                          <option value="SCHOOL_ADMIN">Principal</option>
                          <option value="DISTRICT">District Manager</option>
                          <option value="ADMIN">Admin</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          School
                        </label>
                        <select
                          value={editForm.schoolId}
                          onChange={(e) => setEditForm({ ...editForm, schoolId: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          disabled={editForm.role === 'DISTRICT'}
                        >
                          <option value="">No School</option>
                          {schools.map((school) => (
                            <option key={school.id} value={school.id}>
                              {school.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          District {editForm.role === 'DISTRICT' && '*'}
                        </label>
                        <select
                          required={editForm.role === 'DISTRICT'}
                          value={editForm.districtId}
                          onChange={(e) => setEditForm({ ...editForm, districtId: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                          <option value="">{editForm.role === 'DISTRICT' ? 'Select District' : 'No District'}</option>
                          {districts.map((district) => (
                            <option key={district.id} value={district.id}>
                              {district.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status *
                    </label>
                    <select
                      value={editForm.status}
                      onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="ACTIVE">Active</option>
                      <option value="PENDING_VERIFICATION">Pending Verification</option>
                      <option value="SUSPENDED">Suspended</option>
                      <option value="INACTIVE">Inactive</option>
                    </select>
                  </div>
                </div>
                <div className="border-t border-gray-200 pt-6 mt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Change Password</h3>
                      <p className="text-sm text-gray-500 mt-1">Leave blank to keep current password</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setShowPasswordField(!showPasswordField);
                        if (showPasswordField) {
                          setEditForm({ ...editForm, password: '' });
                          setShowPassword(false);
                        }
                      }}
                      className="text-primary hover:text-primaryDark text-sm font-medium"
                    >
                      {showPasswordField ? 'Cancel' : 'Change Password'}
                    </button>
                  </div>
                  {showPasswordField && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        New Password
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={editForm.password}
                          onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                          className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder="Enter new password (minimum 6 characters)"
                          minLength={6}
                          autoComplete="new-password"
                          name={`user-password-${id}`}
                          id={`user-password-${id}`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                        >
                          {showPassword ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={handleSave}
                    className="bg-primary text-white px-6 py-2 rounded-lg font-semibold hover:bg-primaryDark transition-colors"
                  >
                    Save Changes
                  </button>
                  <button
                    onClick={handleCancel}
                    className="border border-gray-300 text-gray-700 px-6 py-2 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">First Name</label>
                  <p className="text-gray-900 font-medium">{userData.user.firstName}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Last Name</label>
                  <p className="text-gray-900 font-medium">{userData.user.lastName}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Email</label>
                  <p className="text-gray-900">{userData.user.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Phone Number</label>
                  <p className="text-gray-900">{userData.user.phoneNumber || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">School District</label>
                  <p className="text-gray-900">{userData.user.schoolDistrict || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Role</label>
                  <div className="mt-1">{getRoleBadge(userData.user.role)}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Status</label>
                  <div className="mt-1">{getStatusBadge(userData.user.status)}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">School</label>
                  <p className="text-gray-900">{userData.user.school?.name || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Final Exam Result</label>
                  {userData.user.examAttempts && userData.user.examAttempts.length > 0 ? (
                    <div className="flex items-center space-x-2">
                      <span className={`text-lg font-bold ${userData.user.examAttempts[0].passed ? 'text-green-600' : 'text-red-600'}`}>
                        {userData.user.examAttempts[0].score}%
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${userData.user.examAttempts[0].passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {userData.user.examAttempts[0].passed ? 'Passed' : 'Failed'}
                      </span>
                    </div>
                  ) : (
                    <p className="text-gray-400">Not taken</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Date of Completion</label>
                  <p className="text-gray-900 font-medium">
                    {userData.enrollments.find(e => e.completedAt)?.completedAt
                      ? new Date(userData.enrollments.find(e => e.completedAt).completedAt).toLocaleDateString()
                      : userData.user.certificates?.[0]?.issuedAt
                        ? new Date(userData.user.certificates[0].issuedAt).toLocaleDateString()
                        : 'Not completed'}
                  </p>
                </div>
                {!(isDistrict || isSchoolAdmin) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Account Created</label>
                    <p className="text-gray-900">
                      {new Date(userData.user.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'enrollments' && (
          <div>
            <h2 className="text-xl font-bold text-gray-800 mb-4">Enrollments</h2>
            {userData.enrollments.length === 0 ? (
              <p className="text-gray-500">No enrollments found</p>
            ) : (
              <div className="space-y-4">
                {userData.enrollments.map((enrollment) => (
                  <div
                    key={enrollment.id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-gray-900">{enrollment.courseTitle || enrollment.course?.title || 'Unknown Course'}</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          Enrolled: {new Date(enrollment.enrolledAt).toLocaleDateString()}
                        </p>
                        {enrollment.completedAt && (
                          <p className="text-sm text-green-600 mt-1">
                            Completed: {new Date(enrollment.completedAt).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            enrollment.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {enrollment.isActive ? 'Active' : 'Inactive'}
                        </span>
                        <p className="text-sm text-gray-600 mt-2">
                          {enrollment.purchaseType === 'INDIVIDUAL' ? 'Individual' : 'School Bulk'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'progress' && (
          <div>
            <h2 className="text-xl font-bold text-gray-800 mb-6">Course Progress</h2>
            {userData.enrollments.length === 0 ? (
              <p className="text-gray-500">No progress found</p>
            ) : (
              <div className="space-y-6">
                {userData.enrollments.map((enrollment) => (
                  <div key={enrollment.id} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{enrollment.courseTitle}</h3>
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-bold text-primary">{enrollment.progress.percent}%</span>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full bg-gray-200 rounded-full h-3 mb-6">
                      <div 
                        className={`h-3 rounded-full transition-all duration-700 ${enrollment.completedAt ? 'bg-green-500' : 'bg-primary'}`}
                        style={{ width: `${enrollment.progress.percent}%` }}
                      ></div>
                    </div>

                    {/* Latest Activity */}
                    {enrollment.progress.latestActivity ? (
                      <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm transition-shadow hover:shadow-md">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-4 flex items-center">
                          <span className="w-1.5 h-1.5 bg-primary rounded-full mr-2"></span>
                          Most Recent Activity
                        </p>
                        
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center mb-1">
                              <span className="text-xs font-bold text-primary bg-primary/5 px-2 py-0.5 rounded mr-3 whitespace-nowrap">
                                Lesson {enrollment.progress.latestActivity.globalLessonNumber}
                              </span>
                              <p className="text-base font-bold text-gray-900 truncate">
                                {enrollment.progress.latestActivity.lessonTitle}
                              </p>
                            </div>
                            <div className="flex items-center">
                              <span className="text-[10px] font-bold text-gray-400 bg-gray-50 px-2 py-0.5 rounded mr-3 whitespace-nowrap">
                                Module {enrollment.progress.latestActivity.moduleOrder}
                              </span>
                              <p className="text-sm text-gray-500 font-medium truncate">
                                {enrollment.progress.latestActivity.moduleTitle}
                              </p>
                            </div>
                          </div>
                          
                          {/* <div className="ml-6 flex flex-col items-end shrink-0"> */}
                            {/* <span className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                              enrollment.progress.latestActivity.status === 'COMPLETED' 
                                ? 'bg-green-50 text-green-700 border border-green-100' 
                                : 'bg-blue-50 text-blue-700 border border-blue-100'
                            }`}>
                              {enrollment.progress.latestActivity.status}
                            </span> */}
                            {/* <div className="flex items-center mt-3 text-[10px] text-gray-400 font-medium">
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              {new Date(enrollment.progress.latestActivity.updatedAt).toLocaleDateString()}
                            </div> */}
                          {/* </div> */}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4 bg-white rounded-lg border border-dashed border-gray-200">
                        <p className="text-sm text-gray-400 italic">No activity recorded yet</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'certificates' && (
          <div>
            <h2 className="text-xl font-bold text-gray-800 mb-4">Certificates</h2>
            {userData.certificates.length === 0 ? (
              <p className="text-gray-500">No certificates found</p>
            ) : (
              <div className="space-y-4">
                {userData.certificates.map((cert) => (
                  <div
                    key={cert.id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-gray-900">{cert.course.title}</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          Certificate Number: {cert.certificateNumber}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          Issued: {new Date(cert.issuedAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false })}
        onConfirm={handleDeleteUser}
        title="Delete User"
        message={`Are you sure you want to delete ${userData?.user?.firstName} ${userData?.user?.lastName}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      {/* Notification */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={hideNotification}
          duration={notification.duration}
        />
      )}
    </div>
  );
};

export default UserDetail;





